/**
 * Recruiter email-domain gate: does this email belong to this school?
 *
 * Pure and I/O-free so it can back all three enforcement points — the signup
 * page's pre-check, the Supabase before-user-created hook, and the server-side
 * check at /me and /callback — without the rule existing in more than one place.
 *
 * Always evaluate this against a *verified* email (the JWT's), never one taken
 * from a request body.
 */

/** The subset of a school row the gate needs. */
export interface SchoolEmailMatchable {
  /** Every domain accepted for this school. Subdomains of any entry also match. */
  emailDomains?: string[] | null
}

/**
 * Reduce a stored website/domain value to a bare comparable host.
 */
export function normalizeDomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .split('/')[0]
    .split('?')[0]
    .replace(/^www\./, '')
    .replace(/\.+$/, '')
}

/**
 * The domain half of an email address, normalized. Splits on the *last* `@` so
 * a quoted local part can't shift the result. Returns '' when there's no domain.
 */
export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@')
  if (at < 0 || at === email.length - 1) return ''
  return normalizeDomain(email.slice(at + 1))
}

/**
 * True when `email`'s domain is one of the school's accepted domains, or a
 * subdomain of one — university mail routinely lives on a departmental
 * subdomain (`coach@athletics.school.edu`).
 *
 * The match is anchored on a dot so `notuoregon.edu` can never satisfy
 * `uoregon.edu`. It does not work upward: an email at `mit.edu` won't match a
 * school that only lists `web.mit.edu`, which is why the importer seeds parent
 * domains alongside the raw source value.
 */
export function isEmailAllowedForSchool(
  email: string | null | undefined,
  school: SchoolEmailMatchable | null | undefined
): boolean {
  if (!email || !school) return false

  const candidate = emailDomain(email)
  if (!candidate) return false

  return (school.emailDomains ?? []).some(accepted => {
    const domain = normalizeDomain(accepted ?? '')
    if (!domain) return false
    return candidate === domain || candidate.endsWith(`.${domain}`)
  })
}
